import Footer from "../../components/footer/Footer";
import Result from "../../components/result/Result";
import SearchBar from "../../components/searchBar/SearchBar";
import TopBar from "../../components/topBar/topBar";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { apiClient } from "../../api/apiClient";
import ProjectsModal from "../../components/projectsModal/ProjectsModal";

/* ── Skeleton result card ────────────────────────────────────────────────── */
function SkeletonResult() {
  return (
    <div className="animate-pulse rounded-result bg-mainWhite shadow-result border border-mainDark/8 px-5 pt-4 pb-6 mb-6">
      <div className="h-3 w-28 rounded-full bg-mainDark/8 mb-3" />
      <div className="h-7 w-3/4 rounded-full bg-mainDark/10 mb-2" />
      <div className="h-7 w-1/2 rounded-full bg-mainDark/8 mb-4" />
      <div className="h-3 w-20 rounded-full bg-mainDark/6 mb-4" />
      <div className="flex gap-2 mb-5">
        <div className="h-6 w-16 rounded-md bg-mainDark/6" />
        <div className="h-6 w-20 rounded-md bg-mainDark/6" />
        <div className="h-6 w-12 rounded-md bg-mainDark/6" />
      </div>
      <div className="space-y-2">
        <div className="h-3 w-full rounded-full bg-mainDark/6" />
        <div className="h-3 w-4/5 rounded-full bg-mainDark/6" />
      </div>
    </div>
  );
}

/* ── Empty state ─────────────────────────────────────────────────────────── */
const SearchOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

function EmptyState({ query }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-mainDark/5 text-mainDark/20">
        <span className="h-8 w-8"><SearchOffIcon /></span>
      </div>
      <p className="text-xl font-extralight text-mainDark/50">
        {t("resultsPage.noResults")}
      </p>
      {query && (
        <p className="mt-2 text-sm font-light text-mainDark/30">
          {t("resultsPage.headingPrefix")}{" "}
          <span className="italic">{query}</span>
        </p>
      )}
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
function ResultPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [searchPhrase, setSearchPhrase] = useState("");
  const [results, setResults] = useState(null);
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const { isSignedIn, isLoaded } = useAuth();
  const [modalBookId, setModalBookId] = useState(null);

  const query = searchParams.get("query");

  useEffect(() => {
    setSearchPhrase(query || "");

    async function getSearchResults() {
      if (!query) {
        setResults(null);
        setResultsLoaded(false);
        return;
      }
      setResultsLoaded(false);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_SERVER_URI}/search`,
          { search: query },
        );
        if (res.status === 200) {
          setResults(res.data.data ?? []);
        } else {
          setResults([]);
        }
      } catch {
        setResults([]);
      }
      setResultsLoaded(true);

      if (isLoaded && isSignedIn && query && query.trim()) {
        apiClient
          .post("/search-history", { query: query.trim() })
          .catch(() => {});
      }
    }

    getSearchResults();
  }, [query, isLoaded, isSignedIn]);

  return (
    <div className="min-h-screen">

      {/* ── Sticky header ── */}
      <div className="sticky top-0 z-20 backdrop-blur-sm">
        <div className="mainCont">
          <TopBar logo={true} />
          <div className="hidden md:block">
            <SearchBar small={true} />
          </div>
          <div className="block md:hidden pb-3">
            <SearchBar small={false} />
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="mainCont pb-28">
        <div className="grid grid-cols-12">
          <div className="col-span-12 md:col-span-10 md:col-start-2 py-8">

            {/* Results heading */}
            {query && (
              <div className="mb-8">
                {resultsLoaded && Array.isArray(results) && results.length > 0 && (
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-mainDark/30">
                    {results.length} {results.length === 1 ? t("resultsPage.resultSingular") : t("resultsPage.resultPlural")}
                  </p>
                )}
                <h1 className="text-2xl md:text-3xl font-extralight text-mainDark leading-snug">
                  {t("resultsPage.headingPrefix")}{" "}
                  <span className="text-mainBlue">&ldquo;{searchPhrase}&rdquo;</span>
                  {t("resultsPage.headingSuffix")}
                </h1>
              </div>
            )}

            {/* Loading skeletons */}
            {!resultsLoaded && query && (
              <div>
                {[0, 1, 2, 3].map((i) => <SkeletonResult key={i} />)}
              </div>
            )}

            {/* Empty */}
            {resultsLoaded && Array.isArray(results) && results.length === 0 && (
              <EmptyState query={searchPhrase} />
            )}

            {/* Results */}
            {Array.isArray(results) && results.length > 0 && (
              <div>
                {results.map((result, i) => (
                  <div
                    key={result._id}
                    style={{ animation: `resultIn 0.35s ease both ${Math.min(i * 45, 400)}ms` }}
                  >
                    <Result
                      bookData={result}
                      onSaveToProject={(id) => setModalBookId(id)}
                    />
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      <Footer />

      <ProjectsModal
        bookId={modalBookId}
        isOpen={!!modalBookId}
        onClose={() => setModalBookId(null)}
      />

      <style>{`
        @keyframes resultIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ResultPage;
